/**
 * On-demand listing card image slider.
 * Keeps initial DOM lightweight by deferring track creation and slide loading
 * until the user explicitly interacts with the slider controls or swipes.
 */

function initSlider(container) {
  if (!container || container.dataset.sliderInitialized === "true") {
    return container.querySelector(".listingcard__slider-track");
  }

  const dataScript = container.querySelector(".listingcard__slider-data");
  if (!dataScript) {
    return null;
  }

  let pictures = [];
  try {
    pictures = JSON.parse(dataScript.textContent || "[]");
  } catch (err) {
    return null;
  }

  if (!pictures || pictures.length <= 1) {
    return null;
  }

  container.dataset.sliderInitialized = "true";
  container.dataset.sliderIndex = "0";

  const cardLink = container
    .closest(".listingcard")
    ?.querySelector(".listingcard__link");
  const rentalUrl = cardLink ? cardLink.getAttribute("href") || "#" : "#";

  const track = document.createElement("div");
  track.className = "listingcard__slider-track";

  // Slide 0: reuse existing picture or link to avoid flicker/re-download
  const slide0 = document.createElement("div");
  slide0.className = "listingcard__slider-slide";

  const existingLink = container.querySelector(".listingcard__image-link");
  const existingPicture = container.querySelector("picture");

  if (existingLink) {
    slide0.appendChild(existingLink);
  } else if (existingPicture) {
    const link = document.createElement("a");
    link.href = rentalUrl;
    link.className = "listingcard__image-link";
    link.target = "_blank";
    link.tabIndex = -1;
    link.setAttribute("aria-hidden", "true");
    link.appendChild(existingPicture);
    slide0.appendChild(link);
  } else if (pictures[0]) {
    slide0.innerHTML = createPictureMarkup(pictures[0], "eager", rentalUrl);
  }
  track.appendChild(slide0);

  // Slides 1..N: render with lazy loading
  for (let i = 1; i < pictures.length; i++) {
    const slide = document.createElement("div");
    slide.className = "listingcard__slider-slide";
    slide.innerHTML = createPictureMarkup(pictures[i], "lazy", rentalUrl);
    track.appendChild(slide);
  }

  // Insert track before controls / bookmarks
  const controls = container.querySelector(".listingcard__slider-controls");
  if (controls) {
    container.insertBefore(track, controls);
  } else {
    container.prepend(track);
  }

  // Synchronize counter on manual touch swipe or scroll
  let ticking = false;
  track.addEventListener(
    "scroll",
    () => {
      // If programmatic button navigation is in progress, ignore scroll events to prevent counter flicker
      if (track._isProgrammatic) return;

      if (!ticking) {
        window.requestAnimationFrame(() => {
          if (track._isProgrammatic) {
            ticking = false;
            return;
          }
          const width = track.clientWidth || 1;
          const activeIndex = Math.round(track.scrollLeft / width);
          const total = track.children.length;
          if (activeIndex >= 0 && activeIndex < total) {
            const prevIndex = parseInt(
              container.dataset.sliderIndex || "0",
              10
            );
            const direction =
              activeIndex > prevIndex
                ? "next"
                : activeIndex < prevIndex
                ? "prev"
                : null;
            container.dataset.sliderIndex = String(activeIndex);
            updateSliderPagination(container, activeIndex, total, direction);
          }
          ticking = false;
        });
        ticking = true;
      }
    },
    { passive: true }
  );

  // Reset programmatic flag when smooth scroll completes
  if ("onscrollend" in window) {
    track.addEventListener(
      "scrollend",
      () => {
        track._isProgrammatic = false;
        clearTimeout(track._programmaticTimeout);
      },
      { passive: true }
    );
  }

  return track;
}

function createPictureMarkup(pic, loadingStrategy, rentalUrl) {
  return `
    <a href="${escapeAttr(rentalUrl)}" class="listingcard__image-link" target="_blank" tabindex="-1" aria-hidden="true">
      <picture>
        <source srcset="${pic.webp}" type="image/webp">
        <source srcset="${pic.jpg}" type="image/jpeg">
        <img
          class="listingcard__imagecontainer-image"
          loading="${loadingStrategy}"
          src="${pic.src}"
          alt="${pic.alt ? escapeAttr(pic.alt) : ""}"
          draggable="false"
        >
      </picture>
      ${pic.samplePhotoBadge || ""}
    </a>
  `.trim();
}

function escapeAttr(text) {
  return String(text).replace(/"/g, "&quot;");
}

function updateSliderPagination(
  container,
  activeIndex,
  total,
  direction = null
) {
  if (!total || total <= 1) return;

  const currentCounter = container.querySelector(
    ".listingcard__slider-current"
  );
  if (
    currentCounter &&
    currentCounter.textContent !== String(activeIndex + 1)
  ) {
    currentCounter.textContent = String(activeIndex + 1);
  }

  const dots = container.querySelectorAll(".listingcard__slider-dot");
  if (!dots || dots.length === 0) return;

  let activeDotIndex = 0;
  if (total === 2) {
    activeDotIndex = activeIndex === 0 ? 0 : 1;
  } else {
    if (activeIndex === 0) {
      activeDotIndex = 0;
    } else if (activeIndex === total - 1) {
      activeDotIndex = dots.length - 1;
    } else {
      activeDotIndex = 1;
    }
  }

  dots.forEach((dot, idx) => {
    dot.classList.toggle("is-active", idx === activeDotIndex);
  });

  // Shift middle bubble on navigation
  if (direction && dots.length >= 3 && dots[1]) {
    const middleDot = dots[1];
    const shiftClass =
      direction === "next" ? "is-shift-right" : "is-shift-left";
    middleDot.classList.remove("is-shift-right", "is-shift-left");
    void middleDot.offsetWidth;
    middleDot.classList.add(shiftClass);
  }
}

function goToSlide(container, direction) {
  const track = initSlider(container);
  if (!track) return;

  const total = track.children.length;
  if (total <= 1) return;

  let currentIndex = parseInt(container.dataset.sliderIndex || "0", 10);
  let nextIndex = direction === "next" ? currentIndex + 1 : currentIndex - 1;

  // Wrap around
  if (nextIndex >= total) {
    nextIndex = 0;
  } else if (nextIndex < 0) {
    nextIndex = total - 1;
  }

  // Lock scroll events from overwriting counter during smooth scroll
  track._isProgrammatic = true;
  clearTimeout(track._programmaticTimeout);
  track._programmaticTimeout = setTimeout(() => {
    track._isProgrammatic = false;
  }, 400);

  container.dataset.sliderIndex = String(nextIndex);
  updateSliderPagination(container, nextIndex, total, direction);

  const slideWidth = track.clientWidth || container.clientWidth;
  track.scrollTo({
    left: nextIndex * slideWidth,
    behavior: "smooth",
  });
}

// Global touch gestures & swipe-vs-tap handling
let touchStartX = 0;
let touchStartY = 0;
let isSwiping = false;
let swipeTimeout = null;
let touchContainer = null;
let touchSliderWasInitialized = false;

// Record gesture start coordinates. Do not initialize here: moving the image
// into the slider track during touchstart makes the first gesture disappear
// on mobile browsers.
document.addEventListener(
  "touchstart",
  (e) => {
    const container = e.target.closest(
      '.listingcard__imagecontainer[data-slider="on-demand"]'
    );
    if (!container) return;

    if (e.touches && e.touches.length === 1) {
      touchContainer = container;
      touchSliderWasInitialized = container.dataset.sliderInitialized === "true";
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      isSwiping = false;
    }
  },
  { passive: true }
);

// Detect horizontal swipe gesture
document.addEventListener(
  "touchmove",
  (e) => {
    const container = e.target.closest(
      '.listingcard__imagecontainer[data-slider="on-demand"]'
    );
    if (!container || !touchStartX || !e.touches || e.touches.length !== 1)
      return;

    const deltaX = Math.abs(e.touches[0].clientX - touchStartX);
    if (deltaX > 8) {
      isSwiping = true;
      e.stopPropagation();
    }
  },
  { passive: true }
);

// Maintain swipe suppression flag for a short period to catch subsequent click event
document.addEventListener(
  "touchend",
  (e) => {
    const deltaX = e.changedTouches?.[0]
      ? e.changedTouches[0].clientX - touchStartX
      : 0;
    const deltaY = e.changedTouches?.[0]
      ? e.changedTouches[0].clientY - touchStartY
      : 0;
    const isHorizontalSwipe =
      isSwiping && Math.abs(deltaX) > Math.abs(deltaY);

    if (isHorizontalSwipe && touchContainer && !touchSliderWasInitialized) {
      goToSlide(touchContainer, deltaX < 0 ? "next" : "prev");
    }

    if (isHorizontalSwipe) {
      clearTimeout(swipeTimeout);
      swipeTimeout = setTimeout(() => {
        isSwiping = false;
        touchStartX = 0;
        touchStartY = 0;
        touchContainer = null;
      }, 300);
    } else {
      touchStartX = 0;
      touchStartY = 0;
      touchContainer = null;
    }
    touchSliderWasInitialized = false;
  },
  { passive: true }
);

// Global click delegation with capture phase to cancel link clicks during/after swiping
document.addEventListener(
  "click",
  (e) => {
    // Suppress link click on slides when user was swiping
    const link = e.target.closest(".listingcard__image-link");
    if (link && isSwiping) {
      e.preventDefault();
      e.stopPropagation();
      isSwiping = false;
      return;
    }

    const dots = e.target.closest(".listingcard__slider-dots");
    if (dots) {
      e.preventDefault();
      e.stopPropagation();
      const container = dots.closest(".listingcard__imagecontainer");
      if (container) {
        goToSlide(container, "next");
      }
      return;
    }

    const btn = e.target.closest(".listingcard__slider-btn");
    if (!btn) return;

    e.preventDefault();
    e.stopPropagation();

    const container = btn.closest(".listingcard__imagecontainer");
    if (!container) return;

    const isNext = btn.classList.contains("listingcard__slider-btn--next");
    goToSlide(container, isNext ? "next" : "prev");
  },
  true
);
